import { useState, useEffect } from 'react';
import { usersService } from '../../../services';
import { User, UserFormData } from '../../../types';
import { useNotification } from '../../../contexts/NotificationContext';

export const useUsers = () => {
  const { showNotification } = useNotification();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersService.getUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Ошибка при загрузке пользователей');
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (id: string, formData: UserFormData) => {
    try {
      const {
        password: _password,
        permissions: _permissions,
        profile_ids: _profileIds,
        ...userData
      } = formData;
      await usersService.updateUser(id, userData);
      showNotification('Пользователь успешно обновлен', 'success');
      await loadUsers();
      return true;
    } catch (err) {
      console.error('Error updating user:', err);
      showNotification('Ошибка при обновлении пользователя', 'error');
      return false;
    }
  };

  const deleteUser = async (id: string) => {
    try {
      await usersService.deleteUser(id);
      showNotification('Пользователь успешно удален', 'success');
      await loadUsers();
      return true;
    } catch (err) {
      console.error('Error deleting user:', err);
      showNotification('Ошибка при удалении пользователя', 'error');
      return false;
    }
  };

  const toggleUserActive = async (id: string, newState: boolean) => {
    try {
      const updatedUser = await usersService.toggleUserActive(id);
      setUsers((prev) =>
        prev.map((user) =>
          user.id === id
            ? {
                ...user,
                is_active: updatedUser?.is_active ?? newState,
              }
            : user,
        ),
      );
      showNotification(
        `Пользователь успешно ${updatedUser?.is_active ? 'активирован' : 'деактивирован'}`,
        'success'
      );
      return true;
    } catch (err) {
      console.error('Error toggling user active state:', err);
      showNotification('Ошибка при изменении статуса пользователя', 'error');
      return false;
    }
  };

  const toggleUserSuperuser = async (id: string, newState: boolean) => {
    try {
      await usersService.updateUser(id, { is_superuser: newState });
      setUsers((prev) =>
        prev.map((user) =>
          user.id === id
            ? {
                ...user,
                is_superuser: newState,
              }
            : user,
        ),
      );
      showNotification(
        `Права администратора успешно ${newState ? 'предоставлены' : 'отозваны'}`,
        'success'
      );
      return true;
    } catch (err) {
      console.error('Error toggling superuser state:', err);
      showNotification('Ошибка при изменении прав пользователя', 'error');
      return false;
    }
  };

  const resetUser2fa = async (id: string) => {
    try {
      const result = await usersService.resetUser2fa(id);
      setUsers((prev) => prev.map((user) => (
        user.id === id ? { ...user, totp_enabled: false } : user
      )));
      showNotification(
        `2FA отключена. Отозвано доверенных устройств: ${result.revoked_sessions ?? 0}`,
        'success',
      );
      return true;
    } catch (err: any) {
      console.error('Error resetting user 2FA:', err);
      const detail = err?.response?.data?.detail;
      showNotification(typeof detail === 'string' ? detail : 'Не удалось отключить 2FA', 'error');
      return false;
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  return {
    users,
    loading,
    error,
    loadUsers,
    updateUser,
    deleteUser,
    toggleUserActive,
    toggleUserSuperuser,
    resetUser2fa,
  };
};
